// client/src/components/Seo.js
// Sets a page's search engine details: title, description, canonical address,
// social sharing tags, and structured data (JSON-LD) that Google reads.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from '../context/StoreContext';

const DEFAULT_DESCRIPTION = 'RC planes, helicopters, model kits, parts and accessories, delivered across South Africa.';

// Private or empty-of-content pages that shouldn't turn up in search results.
const PRIVATE_PATHS = ['/admin', '/profile', '/cart', '/checkout', '/orders', '/login', '/register', '/forgot-password', '/reset-password', '/documents', '/compare'];

const setMeta = (attribute, key, content) => {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const applyPage = ({ title, description, canonicalPath, image, noindex, storeName }) => {
  const fullTitle = title ? `${title} | ${storeName}` : storeName;
  const url = `${window.location.origin}${canonicalPath}`;
  document.title = fullTitle;
  setMeta('name', 'description', description);
  setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
  setMeta('property', 'og:title', fullTitle);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:site_name', storeName);
  setMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
  if (image) setMeta('property', 'og:image', image);
  setCanonical(url);
};

// Sensible defaults for every page, set each time the page changes.
export const RouteSeo = () => {
  const { pathname } = useLocation();
  const { store } = useStore();
  useEffect(() => {
    applyPage({
      description: DEFAULT_DESCRIPTION,
      canonicalPath: pathname,
      noindex: PRIVATE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`)),
      storeName: store.name,
    });
  }, [pathname, store.name]);
  return null;
};

// Used by a page that has something specific to say. Renders nothing.
const Seo = ({ title, description, canonicalPath, image, noindex, jsonLd }) => {
  const { store } = useStore();
  const { pathname } = useLocation();
  const structuredData = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    applyPage({
      title,
      description: description || DEFAULT_DESCRIPTION,
      canonicalPath: canonicalPath || pathname,
      image,
      noindex,
      storeName: store.name,
    });
  }, [title, description, canonicalPath, image, noindex, store.name, pathname]);

  useEffect(() => {
    if (!structuredData) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = structuredData;
    document.head.appendChild(script);
    return () => script.remove();
  }, [structuredData]);

  return null;
};

export default Seo;
